import { IBoltTask, useBoltApiClient } from "@bolt/core";
import { ExcelQueryCellInfoEventArgs } from "@syncfusion/ej2-react-grids";
import {
  BaseGridModel,
  GridColumn,
  PrimaryGrid,
  PrimaryGridModule,
  GridHeightSettings,
  utils,
  mergeGridColumnsWithDefaults,
} from "@ucl/library";
import { useEffect, useMemo, useRef, useState } from "react";
import { dueInCellExportInterceptor } from "../DueInField/DueInComponent";
import { defaultBoltTaskColumns } from "./config/TaskGridConfig";
import { PrimaryButton } from "@ucl/library";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { openCreateTaskDialog } from "../TaskForm/openCreateTaskDialog";
import { TaskExecutionModuleRegistry } from "../TaskExecutionModule/registry";
import { TaskExecutionModuleFormRegistry } from "../TaskExecutionModule/TaskExecutionModuleProps";
import useTaskGrid from "./useTaskGrid";
import { MyTasksToggle } from "./MyTasksToggle";
import { CommentPermissions } from "../TaskExecutionComponent/TaskExecutionActivityFeed";

export interface BoltTaskGridProps {
  apiBaseUrl: string;
  gridKey: string;
  getAuthRequestHeader: () => Promise<Record<string, string>>;
  onTasksChanged?: () => void;
  taskViewsSettings?: Partial<{
    odataUrl: string;
    gridTitle: string;
    gridAutoName: string;
    shouldCreateAutoView: boolean;
    defaultGridViewTitle: string;
    showGridViewPicker: boolean;
    showUserDefaultView: boolean;
  }>;
  odataUrl?: string;
  // TODO: We should deprecate this and use the boltTaskColumnsConfiguration instead everywhere, maybe.
  columnsConfiguration?: GridColumn[];
  // This one gets merged like Activity Log columns.
  boltTaskColumnsConfiguration?: Record<string, GridColumn>;
  gridLastRefreshed?: number;
  taskExecutionModuleRegistry?: TaskExecutionModuleRegistry;
  formRegistry?: TaskExecutionModuleFormRegistry;
  parentEntityId?: string;
  parentEntityType?: string;
  excludedTaskId?: string;
  isTaskOverviewReadOnly?: boolean;
  taskExecutionLeftPanelIsDocked?: boolean;
  taskExecutionRightPanelIsDocked?: boolean;
  gridHeightSettings?: GridHeightSettings;
  gridConfiguration?: Readonly<BaseGridModel<IBoltTask>>;
  onlyIncludeProvidedColumnsIfSpecified?: boolean;
  showRelatedTasks?: boolean;
  customToolbarComponents?: React.ReactNode;
  // My Tasks functionality
  displayMyTasksToggle?: boolean;
  currentUserId?: string;
  commentPermissions?: CommentPermissions;
}

// TODO: This is a temporary solution, systematically, we either need base api URLS to have a trailing slash or NOT have one.
export const BoltTaskGrid: React.FC<BoltTaskGridProps> = ({
  apiBaseUrl,
  odataUrl,
  columnsConfiguration,
  boltTaskColumnsConfiguration,
  gridKey,
  getAuthRequestHeader,
  taskViewsSettings,
  gridLastRefreshed,
  taskExecutionModuleRegistry,
  formRegistry,
  parentEntityId,
  parentEntityType,
  onTasksChanged,
  isTaskOverviewReadOnly,
  taskExecutionLeftPanelIsDocked,
  taskExecutionRightPanelIsDocked,
  gridHeightSettings,
  gridConfiguration,
  onlyIncludeProvidedColumnsIfSpecified = false,
  showRelatedTasks = false,
  customToolbarComponents,
  displayMyTasksToggle = false,
  currentUserId,
  commentPermissions,
}) => {
  const primaryGridModule = useRef<PrimaryGridModule>(null);
  const boltApiClient = useBoltApiClient();
  const { getPredicate, createOnRecordClick } = useTaskGrid();

  // State for My Tasks toggle
  const [isMyTasksActive, setIsMyTasksActive] = useState<boolean>(false);

  useEffect(() => {
    if (gridLastRefreshed) {
      primaryGridModule.current?.getGridModule()?.getGridRef()?.refresh();
    }
  }, [gridLastRefreshed]);

  const handleTaskComplete = async (_taskId: string) => {
    // Notify parent
    onTasksChanged?.();
  };

  const predicate = useMemo(() => {
    return getPredicate(
      parentEntityType,
      parentEntityId,
      undefined,
      isMyTasksActive,
      currentUserId
    );
  }, [parentEntityType, parentEntityId, isMyTasksActive, currentUserId]);

  const predicateKey = useMemo(() => JSON.stringify(predicate), [predicate]);

  return (
    <section className="bolt-task-grid">
      <div className="bolt-task-grid-header">
        <PrimaryButton
          text="Add Task"
          iconProps={{
            icon: faPlus,
          }}
          onClick={() => {
            openCreateTaskDialog({
              apiBaseUrl,
              getAuthRequestHeader,
              parentEntityId,
              parentEntityType,
              onSave: () => {
                primaryGridModule.current
                  ?.getGridModule()
                  ?.getGridRef()
                  ?.refresh();
                onTasksChanged?.();
              },
            });
          }}
        />
      </div>
      <div className="bolt-task-grid-content">
        <PrimaryGrid<IBoltTask, {}>
          key={predicateKey}
          gridKey={gridKey || "bolt-task-grid"}
          ref={primaryGridModule}
          apiClient={boltApiClient}
          viewsSettings={{
            gridTitle: "Task",
            odataUrl: `${utils.strings.trimEndForwardSlashForURL(
              apiBaseUrl
            )}/bolt/userview/grid`,
            showGridViewPicker: true,
            ...taskViewsSettings,
          }}
          odataUrl={
            odataUrl ||
            `${utils.strings.trimEndForwardSlashForURL(
              apiBaseUrl
            )}/odata/bolttaskodata`
          }
          getAuthRequestHeader={getAuthRequestHeader}
          columnsConfiguration={[
            ...Object.values(
              mergeGridColumnsWithDefaults(
                boltTaskColumnsConfiguration,
                defaultBoltTaskColumns,
                { onlyIncludeProvidedColumnsIfSpecified }
              )
            ),
            ...(columnsConfiguration || []),
          ]}
          predicate={getPredicate(
            parentEntityType,
            parentEntityId,
            undefined,
            isMyTasksActive,
            currentUserId
          )}
          gridHeightSettings={gridHeightSettings}
          gridConfiguration={{
            enableColumnVirtualization: false,
            allowExcelExport: true,
            excelQueryCellInfo: (ev: ExcelQueryCellInfoEventArgs) => {
              // Transforms the targeted cell to the value we want to export it has.
              if (ev.column.field === "dueIn") {
                ev.value = dueInCellExportInterceptor(
                  ev.value as number,
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error
                  BoltTaskStatuses[(ev.data as Task).taskStatus]
                );
              }
            },
            onRecordClick: createOnRecordClick({
              apiBaseUrl,
              odataUrl,
              columnsConfiguration,
              gridKey,
              getAuthRequestHeader,
              taskViewsSettings,
              gridLastRefreshed,
              taskExecutionModuleRegistry,
              formRegistry,
              parentEntityId,
              parentEntityType,
              onTasksChanged,
              isTaskOverviewReadOnly,
              onlyIncludeProvidedColumnsIfSpecified,
              showRelatedTasks,
              handleTaskComplete,
              refreshGrid: () => {
                primaryGridModule.current
                  ?.getGridModule()
                  ?.getGridRef()
                  ?.refresh();
              },
              taskExecutionLeftPanelIsDocked,
              taskExecutionRightPanelIsDocked,
              commentPermissions,
            }),
            ...gridConfiguration, // Merge with passed-in configuration
          }}
          showGridFilters
          customToolbarComponents={
            !!customToolbarComponents || displayMyTasksToggle && (
              <div className="task-grid-toolbar">
                {customToolbarComponents}
                {displayMyTasksToggle && currentUserId && (
                  <MyTasksToggle
                    isActive={isMyTasksActive}
                    onToggle={setIsMyTasksActive}
                  />
                )}
              </div>
            )
          }
        />
      </div>
    </section>
  );
};
